import React, {
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
  useMemo,
} from 'react';
import ReactApexChart from 'react-apexcharts';
import styles from './DailyConversations.module.scss';
import { dashboardEndpoints } from 'hooks/endpoints';
import { useFetch } from 'hooks/useFetch';
import moment from 'moment';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import Tooltip from 'component/Custom/tooltip/Tooltip';
import ChartModal from '../ChartModal';
import FilterDropdown from 'component/Custom/FilterDropdown/FilterDropdown';
import NoData from 'component/Custom/NoData/NoData';
import Loader from 'component/atom/loader/Loader';

const normalizeRows = (rows = []) =>
  rows
    .map((d) => ({
      x: moment.utc(d.date).startOf('day').valueOf(),
      y: d.chatCount ?? 0,
    }))
    .sort((a, b) => a.x - b.x)
    .filter((pt, i, arr) => i === 0 || pt.x !== arr[i - 1].x);

const DailyConversations = ({
  brandingData,
  botId,
  startDate,
  endDate,
  days,
  snapshotMode,
  setLoader,
  botType,
}) => {
  const containerRef = useRef(null);
  const [series, setSeries] = useState([]);
  const [modalSeries, setModalSeries] = useState([]);
  const [showChartModal, setShowChartModal] = useState(false);

  const [modalDays, setModalDays] = useState(null);
  const [modalStartDate, setModalStartDate] = useState(null);
  const [modalEndDate, setModalEndDate] = useState(null);

  const {
    data: conversationsPerDay,
    refetch: refetchConversationsPerDay,
    isFetching,
    isLoading,
  } = useFetch(
    ['conversationsPerDay', startDate, endDate, botId, botType],
    dashboardEndpoints.getConversationsPerDay(startDate, endDate),
    { enabled: false },
    botId ? { botId, chatSource: botType } : {},
  );

  const {
    data: conversationsPerDayModal,
    refetch: refetchConversationsPerDayModal,
  } = useFetch(
    ['conversationsPerDayModal', modalStartDate, modalEndDate, botId, botType],
    dashboardEndpoints.getConversationsPerDay(modalStartDate, modalEndDate),
    { enabled: false },
    botId ? { botId, chatSource: botType } : {},
  );

  useEffect(() => {
    refetchConversationsPerDay();
  }, [startDate, endDate, botType, botId]);

  useEffect(() => {
    setModalDays(days);
  }, [days]);

  useEffect(() => {
    if (!showChartModal) return;
    if (!modalStartDate || !modalEndDate) return;
    refetchConversationsPerDayModal();
  }, [modalStartDate, modalEndDate, showChartModal, botType, botId]);

  useEffect(() => {
    const rows = conversationsPerDay?.data || [];
    setSeries(
      rows.length ? [{ name: 'Conversations', data: normalizeRows(rows) }] : [],
    );
  }, [conversationsPerDay]);

  useEffect(() => {
    const rows = conversationsPerDayModal?.data || [];
    setModalSeries(
      rows.length ? [{ name: 'Conversations', data: normalizeRows(rows) }] : [],
    );
  }, [conversationsPerDayModal]);
  useLayoutEffect(() => {
    const onStart = () => {
      const el = containerRef.current;
      if (!el) return;

      const page = el.closest('[data-export-page]');
      const box = (page || el.parentElement || el).getBoundingClientRect();
      const W = Math.max(Math.floor(box.width || el.scrollWidth || 1200), 320);

      el.style.width = `${W}px`;
      el.style.maxWidth = `${W}px`;
      el.style.overflow = 'hidden';

      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    };

    const onEnd = () => {
      const el = containerRef.current;
      if (!el) return;
      el.style.width = '';
      el.style.maxWidth = '';
      el.style.overflow = '';
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    };

    window.addEventListener('export:start', onStart);
    window.addEventListener('export:end', onEnd);
    return () => {
      window.removeEventListener('export:start', onStart);
      window.removeEventListener('export:end', onEnd);
    };
  }, []);

  const brand = brandingData?.page_ButtonColor || '#0744E1';

  const baseOptions = {
    chart: {
      type: 'area',
      height: 370,
      toolbar: { show: false },
      background: '#FFFFFF',
      zoom: { enabled: false },
    },
    dataLabels: { enabled: false },
    stroke: {
      curve: 'straight',
      width: 4,
      lineCap: 'round',
      colors: [brand],
    },
    markers: { size: 0, hover: { size: 0 } },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.85,
        opacityTo: 0.5,
        stops: [0, 90, 100],
      },
      colors: [brand],
    },
    yaxis: {
      labels: {
        offsetX: -15,
        style: {
          fontSize: '12px',
          color: '#000',
          fontWeight: 400,
          fontFamily: 'Poppins, sans-serif',
        },
      },
    },
    grid: {
      borderColor: '#E9EEF5',
      strokeDashArray: 4,
      padding: { top: 4, right: 4, bottom: 4, left: 4 },
    },
    tooltip: {
      x: {
        formatter: (val, opts) => {
          const labels = opts?.w?.globals?.categoryLabels || [];
          const label = labels[opts.dataPointIndex];
          return label || val || '';
        },
      },
    },
    legend: { show: false },
  };
  const mainData = useMemo(() => {
    const pts = series[0]?.data || [];
    return {
      labels: pts.map((p) => moment.utc(p.x).format('DD-MMM')),
      y: pts.map((p) => p.y ?? 0),
    };
  }, [series]);

  const modalData = useMemo(() => {
    const pts = modalSeries[0]?.data || [];
    return {
      labels: pts.map((p) => moment.utc(p.x).format('DD-MMM')),
      y: pts.map((p) => p.y ?? 0),
    };
  }, [modalSeries]);

  const mainChartOptions = {
    ...baseOptions,
    chart: { ...baseOptions.chart, id: 'daily-conversations-chart' },
    xaxis: {
      type: 'category',
      categories: mainData.labels,
      tickPlacement: 'on',
      labels: {
        style: {
          fontSize: '12px',
          color: '#000',
          fontWeight: 400,
          fontFamily: 'Poppins, sans-serif',
        },
      },
      axisBorder: { show: true, color: '#222' },
      axisTicks: { show: false },
    },
  };

  const modalChartOptions = {
    ...baseOptions,
    chart: { ...baseOptions.chart, id: 'daily-conversations-modal-chart' },
    xaxis: {
      type: 'category',
      categories: modalData.labels,
      tickPlacement: 'on',
      labels: {
        style: {
          fontSize: '12px',
          color: '#000',
          fontWeight: 400,
          fontFamily: 'Poppins, sans-serif',
        },
      },
      axisBorder: { show: true, color: '#222' },
      axisTicks: { show: false },
    },
  };

  const toCSV = (points) => {
    const header = 'Date,Conversations';
    const lines = (points || []).map(
      (p) => `${moment.utc(p.x).format('DD-MMM')},${p.y ?? 0}`,
    );
    return [header, ...lines].join('\n');
  };

  const downloadCSV = (filename, csv) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', filename);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownload = () => {
    if (!series.length) return;
    const csv = toCSV(series[0].data || []);
    downloadCSV('daily_conversations.csv', csv);
  };

  const handleDownloadModal = () => {
    if (!modalSeries.length) return;
    const csv = toCSV(modalSeries[0].data || []);
    downloadCSV('daily_conversations.csv', csv);
  };
  useEffect(() => {
    if (setLoader) setLoader(isFetching);
  }, [isFetching]);

  return (
    <div ref={containerRef} className={styles['chart-container']}>
      <div className={styles['actions-container']}>
        <div className={styles['title-container']}>
          <span
            className={`${styles['title']} ${
              snapshotMode && styles['snapshotMode-title']
            }`}
          >
            Number of Daily Conversations
          </span>
          {!snapshotMode && (
            <Tooltip
              content='Total chatbot conversations initiated each day, 
showing user interaction volume over time.'
              className={styles['tooltip']}
            >
              <SvgWithBranding type='info' color='#682ECB' width={22} />
            </Tooltip>
          )}
        </div>
        {!snapshotMode && (
          <div className={styles['actions-container']}>
            {series.length === 0 ? (
              <Tooltip content='No data available to export'>
                <SvgWithBranding
                  className={`${styles['download-icon']} ${styles['disabled']}`}
                  type='download2'
                  onClick={() => {}}
                />
              </Tooltip>
            ) : (
              <SvgWithBranding
                className={`${styles['download-icon']}`}
                type='download2'
                onClick={handleDownload}
              />
            )}
            <SvgWithBranding
              className={styles['download-icon']}
              type='maximize'
              onClick={() => {
                setModalStartDate((prev) => prev || startDate);
                setModalEndDate((prev) => prev || endDate);
                setShowChartModal(true);
              }}
            />
          </div>
        )}
      </div>
      {snapshotMode && (
        <p className='snap-desc'>
          Total chatbot conversations initiated each day, showing user
          interaction volume over time.
        </p>
      )}

      {isFetching || isLoading ? (
        <Loader style={{ marginTop: '80px' }} />
      ) : series.length === 0 ? (
        <NoData message='No Conversations Recorded' minHeight={370} />
      ) : (
        <ReactApexChart
          options={mainChartOptions}
          series={[{ name: 'Conversations', data: mainData.y }]}
          type='area'
          height={snapshotMode ? 600 : 370}
        />
      )}
      <ChartModal
        show={showChartModal}
        handleClose={() => setShowChartModal(false)}
        modalBodyClassName={styles['modal']}
        child={
          <>
            <div className={styles['actions-container']}>
              <span className={styles['modal-title']}>
                Number of Daily Conversations
              </span>
              <div className={styles['actions']}>
                <FilterDropdown
                  days={modalDays ?? days}
                  handleSetDays={setModalDays}
                  handleOpenCustomDates={() => {}}
                  startDate={modalStartDate}
                  setStartDate={setModalStartDate}
                  endDate={modalEndDate}
                  setEndDate={setModalEndDate}
                  variant='secondary'
                />
                {modalSeries.length === 0 ? (
                  <Tooltip content='No data available to export'>
                    <SvgWithBranding
                      className={`${styles['download-icon']} ${styles['disabled']}`}
                      type='download2'
                      onClick={() => {}}
                    />
                  </Tooltip>
                ) : (
                  <SvgWithBranding
                    className={`${styles['download-icon']}`}
                    type='download2'
                    onClick={handleDownloadModal}
                  />
                )}
                <SvgWithBranding
                  className={styles['close-icon']}
                  type='exit_modal'
                  onClick={() => {
                    setModalStartDate(null);
                    setModalEndDate(null);
                    setModalDays(days);
                    setShowChartModal(false);
                  }}
                />
              </div>
            </div>

            {modalSeries.length === 0 ? (
              <NoData message='No Conversations Recorded' minHeight={450} />
            ) : (
              <ReactApexChart
                options={modalChartOptions}
                series={[{ name: 'Conversations', data: modalData.y }]}
                type='area'
                height={470}
              />
            )}
          </>
        }
      />
    </div>
  );
};

export default DailyConversations;
